/**
 * Adjust Meal Dialog Component
 *
 * Portal-based dialog for AI-powered meal refinement with guidance.
 * Features textarea with 140 char limit, character counter, fixed 30s ETA label,
 * loading dialog with progress bar, and unsaved changes confirmation.
 *
 * @module components/meal-plans/AdjustMealDialog
 */

import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/hooks/useTheme';
import { Button } from '@/components/ui/Button';
import { TextArea } from '@/components/ui/TextArea';
import { LoadingDialog } from '@/components/ui/LoadingDialog';
import { CloseButton } from '@/components/ui/CloseButton';
import { useMealPlanStore } from '@/stores/mealPlanStore';
import type { MealPlanFrontend, AdjustMealFormData } from '@/types/mealPlan';

/**
 * Props for AdjustMealDialog component
 */
export interface AdjustMealDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** The meal to adjust */
  meal: MealPlanFrontend['mealPlanData']['meals'][0];
  /** ID of the meal plan containing this meal */
  mealPlanId: string;
  /** Callback when meal is successfully regenerated */
  onSuccess?: () => void;
  /** Callback when regeneration fails */
  onError?: (error: Error) => void;
}

/**
 * Adjust Meal Dialog Component
 *
 * Allows dietitians to refine existing meals using AI guidance while
 * maintaining meal identity and dietary restrictions.
 */
export const AdjustMealDialog: React.FC<AdjustMealDialogProps> = ({
  isOpen,
  onClose,
  meal,
  mealPlanId,
  onSuccess,
  onError,
}) => {
  const { isDark } = useTheme();
  const [guidance, setGuidance] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const regenerateMeal = useMealPlanStore((state) => state.regenerateMeal);
  const operations = useMealPlanStore((state) => state.operations);
  const errors = useMealPlanStore((state) => state.errors);

  const operationKey = `${mealPlanId}-${meal.id}`;
  const isRegenerating = operations.regenerating[operationKey] || false;
  const error = errors[operationKey];

  // Character limits (must match backend MealRefinementRequest.guidance)
  const MIN_GUIDANCE_LENGTH = 10;
  const MAX_GUIDANCE_LENGTH = 560;

  // Reset form and focus textarea when dialog opens
  useEffect(() => {
    if (isOpen) {
      setGuidance('');
      setHasUnsavedChanges(false);
      setShowConfirmClose(false);
      setValidationError(null);

      // Focus textarea after a short delay to ensure DOM is ready
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(guidance.trim().length > 0);
  }, [guidance]);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isRegenerating) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isRegenerating]);

  // Validate guidance
  const validateGuidance = (value: string): string | null => {
    if (value.trim().length === 0) {
      return 'Guidance is required';
    }
    if (value.trim().length < MIN_GUIDANCE_LENGTH) {
      return `Guidance must be at least ${MIN_GUIDANCE_LENGTH} characters`;
    }
    if (value.trim().length > MAX_GUIDANCE_LENGTH) {
      return `Guidance must be ${MAX_GUIDANCE_LENGTH} characters or less`;
    }
    return null;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const error = validateGuidance(guidance);
    if (error) {
      setValidationError(error);
      return;
    }

    try {
      const formData: AdjustMealFormData = {
        guidance: guidance.trim(),
      };

      await regenerateMeal(mealPlanId, meal.id, formData);

      // Success
      setGuidance('');
      setHasUnsavedChanges(false);
      onSuccess?.();
      onClose();
    } catch (err) {
      onError?.(err as Error);
    }
  };

  // Handle close with unsaved changes confirmation
  const handleClose = () => {
    if (hasUnsavedChanges && !showConfirmClose) {
      setShowConfirmClose(true);
    } else {
      setGuidance('');
      setHasUnsavedChanges(false);
      setShowConfirmClose(false);
      setValidationError(null);
      onClose();
    }
  };

  // Handle discard changes
  const handleDiscardChanges = () => {
    setGuidance('');
    setHasUnsavedChanges(false);
    setShowConfirmClose(false);
    setValidationError(null);
    onClose();
  };

  // Handle cancel discard
  const handleCancelDiscard = () => {
    setShowConfirmClose(false);
  };

  if (!isOpen) {
    return null;
  }

  // Show loading dialog during regeneration
  if (isRegenerating) {
    return (
      <LoadingDialog
        isOpen={true}
        title="Adjusting Meal..."
        message="Refining your meal based on your guidance."
        estimatedTimeSeconds={30}
        isComplete={false}
      />
    );
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="adjust-meal-dialog-title"
      style={{
        position: 'fixed',
        top: '0px',
        left: '0px',
        width: '100vw',
        height: '100vh',
        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: isDark ? '#1f2937' : 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: isDark
            ? '0 20px 25px -5px rgba(0, 0, 0, 0.4)'
            : '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          border: isDark ? '1px solid #374151' : 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Unsaved Changes Confirmation */}
        {showConfirmClose && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <div
              style={{
                backgroundColor: isDark ? '#1f2937' : 'white',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '400px',
                margin: '20px',
                border: isDark ? '1px solid #374151' : 'none',
              }}
            >
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: isDark ? '#f9fafb' : '#111827',
                  margin: '0 0 12px 0',
                }}
              >
                Discard Changes?
              </h3>
              <p
                style={{
                  fontSize: '14px',
                  color: isDark ? '#d1d5db' : '#6b7280',
                  margin: '0 0 20px 0',
                  lineHeight: '1.5',
                }}
              >
                You have unsaved changes. Are you sure you want to discard them?
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <Button variant="ghost" onClick={handleCancelDiscard}>
                  Cancel
                </Button>
                <Button variant="secondary" onClick={handleDiscardChanges}>
                  Discard
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <h2
              id="adjust-meal-dialog-title"
              style={{
                fontSize: '20px',
                fontWeight: '600',
                color: isDark ? '#f9fafb' : '#111827',
                margin: '0 0 4px 0',
              }}
            >
              Adjust Meal
            </h2>
            <p
              style={{
                fontSize: '14px',
                color: isDark ? '#9ca3af' : '#6b7280',
                margin: 0,
              }}
            >
              {meal.mealName}
            </p>
          </div>
          <CloseButton
            onClick={handleClose}
            ariaLabel="Close dialog"
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px' }}>
            {/* Error Message */}
            {error && (
              <div
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backgroundColor: isDark ? '#7f1d1d' : '#fee2e2',
                  marginBottom: '20px',
                  display: 'flex',
                  gap: '12px',
                }}
              >
                <svg
                  style={{ width: '20px', height: '20px', color: '#ef4444', flexShrink: 0 }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p
                  style={{
                    fontSize: '13px',
                    color: isDark ? '#fca5a5' : '#991b1b',
                    margin: 0,
                  }}
                >
                  {error.message || 'Failed to adjust meal. Please try again.'}
                </p>
              </div>
            )}

            {/* Guidance Input */}
            <div style={{ marginBottom: '20px' }}>
              <label
                htmlFor="guidance-input"
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: isDark ? '#f9fafb' : '#111827',
                  marginBottom: '8px',
                }}
              >
                Specific Guidance <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <TextArea
                ref={textareaRef}
                id="guidance-input"
                value={guidance}
                onChange={(e) => {
                  const newValue = e.target.value;
                  // Enforce character limit by truncating
                  if (newValue.length <= MAX_GUIDANCE_LENGTH) {
                    setGuidance(newValue);
                  } else {
                    setGuidance(newValue.slice(0, MAX_GUIDANCE_LENGTH));
                  }
                  setValidationError(null);
                }}
                placeholder="e.g., Add more protein, reduce carbs, make it vegetarian..."
                rows={4}
                maxLength={MAX_GUIDANCE_LENGTH}
                aria-invalid={!!validationError}
                aria-describedby={validationError ? 'guidance-error' : 'guidance-help'}
                style={{
                  borderColor: validationError
                    ? '#ef4444'
                    : isDark
                      ? '#4b5563'
                      : '#d1d5db',
                }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '6px',
                }}
              >
                {validationError ? (
                  <span
                    id="guidance-error"
                    style={{
                      fontSize: '12px',
                      color: '#ef4444',
                    }}
                  >
                    {validationError}
                  </span>
                ) : (
                  <span
                    id="guidance-help"
                    style={{
                      fontSize: '12px',
                      color: isDark ? '#9ca3af' : '#6b7280',
                    }}
                  >
                    Describe what meal refinements you want to generate
                  </span>
                )}
                <span
                  style={{
                    fontSize: '12px',
                    color:
                      guidance.length > MAX_GUIDANCE_LENGTH
                        ? '#ef4444'
                        : isDark
                          ? '#9ca3af'
                          : '#6b7280',
                  }}
                >
                  {guidance.length}/{MAX_GUIDANCE_LENGTH}
                </span>
              </div>
            </div>
            {/* Info Box */}
            <div
              style={{
                padding: '12px 16px',
                borderRadius: '8px',
                backgroundColor: isDark ? '#1e3a8a' : '#dbeafe',
                marginBottom: '20px',
                display: 'flex',
                gap: '12px',
              }}
            >
              <svg
                style={{ width: '20px', height: '20px', color: '#3b82f6', flexShrink: 0 }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p
                  style={{
                    fontSize: '12px',
                    color: isDark ? '#bfdbfe' : '#3b82f6',
                    margin: 0,
                    lineHeight: '1.4',
                  }}
                >
                  Estimated time: ~30 seconds
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
            }}
          >
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={guidance.trim().length < MIN_GUIDANCE_LENGTH || !!validationError}
            >
              Adjust Meal
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AdjustMealDialog;